import { useCallback, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import * as orderService from '@/supabase/order/service/orderService';
import { logError } from '@/lib/sentry';
import type { CustomerTemplate } from '@/components/CustomerTemplateManager';
import type { OrderStatus } from '@/components/OrderControls';

// 서버 status(text) → OrderStatus 좁히기 — enum 밖 값(스키마 드리프트)은 반영하지 않음(fail-closed).
const ORDER_STATUSES: readonly OrderStatus[] = ['미지정', '부분 입금', '입금 완료', '출고 완료'];
const asOrderStatus = (s: string): OrderStatus | null =>
  (ORDER_STATUSES as readonly string[]).includes(s) ? (s as OrderStatus) : null;

interface UseOrderStatusReconcileProps {
  updateDateOrderTemplates: Dispatch<SetStateAction<CustomerTemplate[]>>;
}

interface UseOrderStatusReconcileResult {
  reconcileOrderStatus: (orderId: number) => void;
  // 명시적 status 쓰기(상태 드롭다운·킵 되돌림·합배송) barrier — begin은 낙관 갱신 "직전",
  // end는 DB 쓰기 정착(성공/실패) 후. barrier 중 reconcile 착수는 소거하고 최외곽 end가 무조건
  // 1회 재조회 — in-flight/신규 reconcile이 명시적 변경을 옛 status로 되돌리는 창 폐쇄
  // (토큰 무효화만으로는 쓰기 진행 "중" 시작된 reconcile이 커밋 전 값을 읽어 이길 수 있음 — R2-F5)
  // + begin이 죽인 유일한 reconcile의 손실 복원(R3-P1).
  beginStatusWrite: (orderId: number) => void;
  endStatusWrite: (orderId: number) => void;
}

/**
 * 부분입금 자동 전환 상태 reconcile 도메인 훅 (스펙 §16.10).
 * /order 가격 뮤테이션(addItem/updateItem/removeItem)·배송 override 저장이 커밋되면 서버 트리거가
 * 해당 주문을 '입금 완료'→'부분 입금'으로 자동 강등했을 수 있다 — 뮤테이션 성공 후 status만
 * 서버에서 재조회해 Home 허브(dateOrderTemplates)에 좁게 반영한다(다른 필드 clobber 금지).
 */
export function useOrderStatusReconcile({
  updateDateOrderTemplates,
}: UseOrderStatusReconcileProps): UseOrderStatusReconcileResult {
  // 주문별 최신 호출 토큰 — 연속 편집으로 겹친 reconcile 응답의 stale-write 방지(latest-wins).
  const tokensRef = useRef(new Map<number, number>());
  // 명시적 쓰기 barrier 깊이(중첩 허용: 배치가 같은 주문을 겹쳐 잡는 방어).
  const writeDepthRef = useRef(new Map<number, number>());

  const reconcileOrderStatus = useCallback((orderId: number) => {
    if ((writeDepthRef.current.get(orderId) ?? 0) > 0) {
      return;   // 명시적 쓰기 진행 중 — 최외곽 endStatusWrite가 무조건 1회 재조회하므로 병합 소거
    }
    const token = (tokensRef.current.get(orderId) ?? 0) + 1;
    tokensRef.current.set(orderId, token);
    void orderService.getOrderStatus(orderId)
      .then((raw) => {
        if (raw === null || tokensRef.current.get(orderId) !== token) return;
        const status = asOrderStatus(raw);
        if (status === null) return;
        updateDateOrderTemplates((prev) => {
          const idx = prev.findIndex((c) => c.id === `order_${orderId}`);
          if (idx === -1 || prev[idx].status === status) return prev;   // 무변경 = prev 참조 보존
          const next = [...prev];
          next[idx] = { ...next[idx], status };
          return next;
        });
      })
      .catch((err) => logError('주문 상태 reconcile 실패:', err));   // best-effort — 다음 로드가 수렴
  }, [updateDateOrderTemplates]);

  const beginStatusWrite = useCallback((orderId: number) => {
    writeDepthRef.current.set(orderId, (writeDepthRef.current.get(orderId) ?? 0) + 1);
    // in-flight reconcile 응답 즉시 무효화 — barrier는 "이후 착수"만 막으므로 둘 다 필요.
    tokensRef.current.set(orderId, (tokensRef.current.get(orderId) ?? 0) + 1);
  }, []);

  const endStatusWrite = useCallback((orderId: number) => {
    const depth = (writeDepthRef.current.get(orderId) ?? 0) - 1;
    if (depth > 0) {
      writeDepthRef.current.set(orderId, depth);
      return;
    }
    writeDepthRef.current.delete(orderId);
    // 쓰기 정착 후 잔여 응답 무효화 + 무조건 1회 재조회(권위 수렴 — R3-P1): begin이 죽인 유일한
    // reconcile의 손실을 복원하고, 실패한 쓰기의 낙관 status도 DB 진실로 되돌리며, 명시적 쓰기와
    // 자동 전환이 DB에서 어떤 순서로 커밋됐든 최종 상태를 가져온다. 성공 경로에선 no-op 패치.
    tokensRef.current.set(orderId, (tokensRef.current.get(orderId) ?? 0) + 1);
    reconcileOrderStatus(orderId);
  }, [reconcileOrderStatus]);

  return { reconcileOrderStatus, beginStatusWrite, endStatusWrite };
}
